import { Card, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { BRAND } from '@/brand/brand'

interface Section {
  readonly heading: string
  readonly body: string
}

const TERMS: readonly Section[] = [
  { heading: 'Who you are dealing with', body: `${BRAND.name} is a financial technology company, not a bank. Accounts and cards are provided by regulated partner institutions, and your balance is held with them.` },
  { heading: 'What we charge', body: 'Every price is shown before you confirm anything: 1.0% to send abroad, the corridor spread inside the rate, 0.9% when card spending converts, and nothing to open an account or receive money. We never add a charge at confirmation that was not on the screen before it.' },
  { heading: 'Your money', body: 'Balances are held at partner banks and are not lent out by us. Money in a Vault or a tokenised asset is invested and can fall in value; that is stated on those screens and is not covered by deposit insurance.' },
  { heading: 'Keeping the account safe', body: 'You are responsible for your password and your devices. Tell us within a day of noticing anything you did not do, and freeze your card from the app the moment you suspect it.' },
  { heading: 'Closing your account', body: 'You can close at any time from Profile. We return the balance to an account in your name, and keep records for as long as the regulator requires.' },
]

const PRIVACY: readonly Section[] = [
  { heading: 'What we collect', body: 'What you give us when you open the account, what you do with the product, and what the law requires us to verify. Nothing more.' },
  { heading: 'Why we collect it', body: 'To run the account, to meet anti-money-laundering rules, and to stop fraud. We do not sell your data and we do not build advertising profiles from it.' },
  { heading: 'Who sees it', body: 'Our partner banks and card issuers, identity and sanctions-screening providers, and regulators when they ask. Each one only gets what it needs for that job.' },
  { heading: 'How long we keep it', body: 'For as long as the account is open, and then for the period the regulator requires after it closes. You can export everything we hold from Profile at any time.' },
  { heading: 'Your choices', body: 'Ask for a copy, ask us to correct it, or ask us to delete what we are not legally required to keep. Marketing email is off unless you turn it on.' },
]

export function LegalScreen({ kind }: { readonly kind: 'terms' | 'privacy' }) {
  const isTerms = kind === 'terms'
  const sections = isTerms ? TERMS : PRIVACY

  return (
    <>
      <PageHeader
        title={isTerms ? 'Terms' : 'Privacy'}
        subtitle={
          isTerms
            ? 'The agreement between you and us, in the shortest language we could write it.'
            : 'What we hold about you, why, and what you can ask us to do with it.'
        }
      />

      <Card tone="light">
        <Stack gap={6} style={{ maxWidth: '44rem' }}>
          {sections.map((section, index) => (
            <Stack key={section.heading} gap={2}>
              <Text variant="eyebrow" weight="semibold" tone="tertiary">
                {String(index + 1).padStart(2, '0')}
              </Text>
              <Text variant="subtitle" weight="bold">
                {section.heading}
              </Text>
              <Text variant="body" tone="secondary">
                {section.body}
              </Text>
            </Stack>
          ))}
          <Text variant="caption" tone="tertiary">
            Last updated 22 September 2026. We write here before we change anything material, and
            never apply a change retroactively.
          </Text>
        </Stack>
      </Card>
    </>
  )
}
